// Practice Project Details Page
// Date: January 28, 2026  
// Updated with two-column layout, admin actions, and comprehensive view

import { format, parseISO } from 'date-fns';
import {
  ArrowLeft,
  BarChart,
  BookOpen,
  Calendar,
  CheckCircle,
  Clipboard,
  Clock,
  ExternalLink,
  FileText,
  Flag,
  Gauge,
  HelpCircle,
  Pencil,
  Play,
  Power,
  RefreshCw,
  Server,
  Settings,
  Trash2,
  User,
  XCircle
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  useCreateWorkspaceMutation,
  useAgentSessionStatusQuery,
  getWorkspaceStatus,
  listSessions,
  getActiveWorkspaceSession,
} from '../../api/jupyterManagementApi';
import { useSandboxLifecycle } from '../../hooks/useSandboxLifecycle';
import type { SandboxStatus } from '../../hooks/useSandboxLifecycle';
import {
  useDeletePracticeProjectMutation,
  useEnrollPracticeProjectMutation,
  usePracticeProjectQuery,
  usePublishPracticeProjectMutation,
  useUnpublishPracticeProjectMutation,
  useUpdateLastAccessedMutation,
  useMarkAsCompletedMutation
} from '../../api/practiceProjectsApi';
import { useAuth } from '../../App';
import SandboxTermsModal from '../../components/SandboxTerms';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import { Progress } from '../../components/ui/progress';
import { ProjectFilesSection } from '../../components/ui/ProjectFilesSection';
import { Skeleton } from '../../components/ui/skeleton';
import { ROLES } from '../../constant/roles';
import { useJupyterWorkspace } from '../../contexts/JupyterWorkspaceContext';
import { LocalRunButton } from '../../components/LocalRunButton';
import { AgentStatusBadge } from '../../components/AgentStatusBadge';
import paths from '../../routes';
import { SandboxPanel } from '../../components/sandbox/SandboxPanel';
import {
  formatDifficulty,
  formatRelativeTime,
  formatStatus,
  getDifficultyColor,
  getStatusColor
} from '../../utils/practiceProjects.utils';


// SandboxStatus is now imported from useSandboxLifecycle

export default function PracticeProjectDetailsPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [unpublishDialogOpen, setUnpublishDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [markAsCompletedDialogOpen, setMarkAsCompletedDialogOpen] = useState(false);
  const [isEnrolling, setIsEnrolling] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const [provisioningStartedAt, setProvisioningStartedAt] = useState<number | null>(null);

  // Jupyter workspace context
  const { workspace, updateWorkspace, resetWorkspace, isConnectionVerified } = useJupyterWorkspace();
  const queryClient = useQueryClient();

  // React Query hooks
  const { data, isLoading, isError } = usePracticeProjectQuery(projectId);
  const enrollMutation = useEnrollPracticeProjectMutation();
  const publishMutation = usePublishPracticeProjectMutation();
  const unpublishMutation = useUnpublishPracticeProjectMutation();
  const deleteMutation = useDeletePracticeProjectMutation();
  const markAsCompletedMutation = useMarkAsCompletedMutation();
  const createWorkspaceMutation = useCreateWorkspaceMutation();
  const updateLastAccessedMutation = useUpdateLastAccessedMutation();

  const project = data?.data;
  const isEnrolled = project?.enrollmentStatus?.isEnrolled || false;
  const completionPercentage = project?.enrollmentStatus?.completionPercentage || 0;
  const enrollmentStatusValue = project?.enrollmentStatus?.status;
  const isAdmin = user?.role === ROLES.ADMIN || user?.role === ROLES.INSTRUCTOR;

  // ── Sandbox lifecycle (unified hook) ──────────────────────────────────────
  const sandboxLifecycle = useSandboxLifecycle({
    sandboxId: projectId || "",
    launchFn: () => createWorkspaceMutation.mutateAsync({ projectId: projectId || "" }),
    onActive: (data) => {
      const server = data?.server || {};
      const kernel = data?.kernel || {};
      updateWorkspace({
        project: { id: projectId!, title: project?.title, isPracticeProject: true },
        status: "active",
        instanceId: server?.state?.object_name || null,
        serverId: server?.name || null,
        kernelId: kernel?.id || null,
        sessionId: data.sessionId,
        workspaceUrl: server?.url || null,
        createdAt: server?.started || null,
        lastActivity: kernel?.last_activity || null,
        folderPath: data.folderPath,
        notebookPath: data.notebookPath,
        serverReady: !!server?.ready,
        kernelExecutionState: kernel?.execution_state || null,
        remainingTime: data.remainingTime,
        totalAllowedTime: data.totalAllowedTime,
        isPracticeProject: true,
      });
    },
    onIdle: () => resetWorkspace(),
    onProvisioning: () => {
      if (workspace.status !== "provisioning") {
        updateWorkspace({ 
          status: "provisioning", 
          project: { id: projectId!, title: project?.title, isPracticeProject: true } 
        });
      }
    },
    sessionId: workspace.sessionId,
  });

  const { sandboxStatus, setSandboxStatus, handleLaunch, handleCancel, handleStop } = sandboxLifecycle;

  // ── Sync workspace context → sandboxStatus on mount/context change ──────────
  useEffect(() => {
    if (sandboxStatus.status === "provisioning" || sandboxStatus.status === "stopping") return;
    
    // Safety guard: if we just started provisioning, don't let the context sync revert it
    const isRecentlyStarted = provisioningStartedAt && (Date.now() - provisioningStartedAt < 60000);
    if (isRecentlyStarted) return;

    if (workspace.project?.id === projectId && workspace.status !== "none") {
      setSandboxStatus({ status: workspace.status as any, instanceId: workspace.instanceId || undefined });
    } else if (workspace.project?.id === projectId && workspace.status === "none") {
      setSandboxStatus({ status: "none" });
    } else if (workspace.project?.id !== projectId) {
      setSandboxStatus({ status: "none" });
    }
  }, [workspace.project?.id, workspace.status, workspace.instanceId, projectId, provisioningStartedAt, setSandboxStatus]);

  // Mount-time reconcile: Redis lease + workspaceStatus vs context (heartbeats are off this route).
  useEffect(() => {
    if (!projectId || !project || !isConnectionVerified) return;
    let cancelled = false;

    getActiveWorkspaceSession(projectId).then(async (active) => {
      if (cancelled) return;
      try {
        const probe = await getWorkspaceStatus(projectId);
        if (cancelled) return;

        const forThisProject = workspace.project?.id === projectId;
        const uiStaleSession =
          forThisProject &&
          (workspace.status === "active" || workspace.status === "error");
        const panelThinksLive =
          sandboxStatus.status === "active" || sandboxStatus.status === "error";

        if (!active) {
          const qcPayload =
            probe.status === "provisioning"
              ? probe
              : probe.status === "error"
                ? probe
                : { status: "idle" as const, progress: probe?.progress ?? 0, message: probe?.message };
          queryClient.setQueryData(["jupyter", "workspace", "status", projectId], qcPayload);

          // No `active-ws` lease — do not trust stale `bits:workspace` "active" payloads.
          if (uiStaleSession || panelThinksLive) {
            resetWorkspace();
            setSandboxStatus({ status: "none" });
          } else if (probe.status === "provisioning") {
            setSandboxStatus({ status: "provisioning", provisioningProgress: probe.progress || 10 });
            setProvisioningStartedAt(Date.now());
            updateWorkspace({
              status: "provisioning",
              project: { id: projectId, title: project.title, isPracticeProject: true },
            });
          }
          return;
        }

        if (active && (probe.status === "idle" || probe.status === "error")) {
          resetWorkspace();
          setSandboxStatus({ status: "none" });
          return;
        }

        if (probe.status === "provisioning") {
          setSandboxStatus({ status: "provisioning", provisioningProgress: probe.progress || 10 });
          setProvisioningStartedAt(Date.now());
          updateWorkspace({
            status: "provisioning",
            project: { id: projectId, title: project.title, isPracticeProject: true },
          });
          return;
        }

        if (probe.status !== "active" || !probe.data) {
          return;
        }

        const d = probe.data;
        let recoveredKernelId = d.kernel?.id || "";
        let recoveredNotebookPath = d.notebookPath;

        if (!recoveredKernelId) {
          try {
            const sessions = await listSessions();
            const match = sessions.find((s: any) => s.path?.includes(projectId));
            if (match?.kernel?.id) {
              recoveredKernelId = match.kernel.id;
              if (!recoveredNotebookPath && match.path) recoveredNotebookPath = `/${match.path}`;
            }
          } catch (_) {}
        }

        updateWorkspace({
          project: { id: projectId, title: project.title, isPracticeProject: true },
          kernelId: recoveredKernelId,
          sessionId: d.sessionId || null,
          workspaceUrl: d.server?.url || "",
          status: "active",
          folderPath: d.folderPath,
          notebookPath: recoveredNotebookPath,
          serverReady: d.server?.ready || false,
          kernelExecutionState: d.kernel?.execution_state || "idle",
          remainingTime: d?.remainingTime ?? null,
          totalAllowedTime: d?.totalAllowedTime ?? null,
          timeUsed: d?.timeUsed ?? null,
        });
        setSandboxStatus({ status: "active" });
      } catch (_) {}
    }).catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [
    projectId,
    project,
    isConnectionVerified,
    location.pathname,
    workspace.project?.id,
    workspace.status,
    sandboxStatus.status,
    queryClient,
    updateWorkspace,
    setSandboxStatus,
    resetWorkspace,
  ]);

  // ── Navigate to sandbox (used when sandbox is already active/provisioning) ──
  const handleNavigateToSandbox = useCallback(() => {
    if (!projectId || !project) return;
    if (isEnrolled && projectId) updateLastAccessedMutation.mutate(projectId);
    navigate(`/notebook/${projectId}`, {
      state: { 
        project: { id: projectId, title: project.title, isPracticeProject: true },
        fromDetails: true
      },
    });
  }, [projectId, project, isEnrolled, navigate, updateLastAccessedMutation]);

  // ── Open sandbox: navigate if active, otherwise launch ────────────────────
  const handleOpenSandbox = useCallback(async () => {
    if (!projectId || !project) return;
    if (isEnrolled && projectId) updateLastAccessedMutation.mutate(projectId);
    if (sandboxStatus.status === "active" || sandboxStatus.status === "provisioning") {
      handleNavigateToSandbox();
      return;
    }
    await handleLaunch();
  }, [projectId, project, sandboxStatus.status, handleLaunch, handleNavigateToSandbox, isEnrolled, updateLastAccessedMutation]);


  // Update last accessed time when enrolled users visit the project
  useEffect(() => {
    if (project && isEnrolled && projectId && !isAdmin) {
      updateLastAccessedMutation.mutate(projectId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.id, isEnrolled, projectId, isAdmin]);

  const handleEnroll = useCallback(async () => {
    if (projectId) {
      setIsEnrolling(true);
      try {
        await enrollMutation.mutateAsync(projectId);
        // Show skeleton for minimum 2 seconds for smooth UX
        await new Promise(resolve => setTimeout(resolve, 2000));
      } finally {
        setIsEnrolling(false);
      }
    }
  }, [projectId, enrollMutation]);


  const handleStartLearning = useCallback(() => {
    if (projectId) {
      navigate(`/notebook/${projectId}`, {
        state: {
          project: {
            id: projectId,
            title: project?.title,
            isPracticeProject: true,
          },
          fromDetails: true
        },
      });
    }
  }, [projectId, project?.title, navigate]);

  const handleGoBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const handleEdit = useCallback(() => {
    if (projectId) {
      navigate(paths.toEditPracticeProject(projectId));
    }
  }, [projectId, navigate]);

  const handlePublish = useCallback(async () => {
    if (projectId) {
      await publishMutation.mutateAsync(projectId);
      setPublishDialogOpen(false);
    }
  }, [projectId, publishMutation]);

  const handleUnpublish = useCallback(async () => {
    if (projectId) {
      await unpublishMutation.mutateAsync(projectId);
      setUnpublishDialogOpen(false);
    }
  }, [projectId, unpublishMutation]);

  const handleDelete = useCallback(async () => {
    if (projectId) {
      await deleteMutation.mutateAsync(projectId);
      setDeleteDialogOpen(false);
      navigate(paths.practiceProjectsManagement);
    }
  }, [projectId, deleteMutation, navigate]);

  const handleMarkAsCompleted = useCallback(async () => {
    if (projectId) {
      await markAsCompletedMutation.mutateAsync(projectId);
      setMarkAsCompletedDialogOpen(false);
    }
  }, [projectId, markAsCompletedMutation]);




  if (isLoading || isEnrolling) {
    return <DetailsPageSkeleton />;
  }

  if (isError || !project) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center py-12">
          <p className="text-destructive">Failed to load project details</p>
          <Button onClick={handleGoBack} className="mt-4">
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header with Back Button, Title, and Actions */}
      <div className="flex flex-row items-start gap-3 mb-6 mt-4">
        <div className="flex items-center">
          <button
            onClick={handleGoBack}
            className="p-2 rounded-full hover:bg-gray-100"
          >
            <ArrowLeft className="h-6 w-6 text-gray-700" />
          </button>
        </div>

        <div className="flex flex-col flex-grow">
          <h1 className="text-3xl font-semibold">{project.title}</h1>
          <div className="flex flex-wrap gap-2 mt-2">
            <Badge className={getDifficultyColor(project.difficulty_level)}>
              {formatDifficulty(project.difficulty_level)}
            </Badge>
            <Badge className={getStatusColor(project.status)}>
              {formatStatus(project.status)}
            </Badge>
          </div>
        </div>

        {/* Admin Actions */}
        {isAdmin && (
          <div className="flex items-center gap-3">
            {project.status !== 'published' && (
              <Button variant="outline" size="sm" onClick={handleEdit}>
                <Pencil className="w-4 h-4 mr-2" />
                Edit
              </Button>
            )}
            {project.status === 'draft' && (
              <Button size="sm" onClick={() => setPublishDialogOpen(true)} className="bg-bits-blue hover:bg-blue-700">
                <CheckCircle className="w-4 h-4 mr-2" />
                Publish
              </Button>
            )}
            {project.status === 'published' && (
              <Button variant="secondary" size="sm" onClick={() => setUnpublishDialogOpen(true)}>
                <XCircle className="w-4 h-4 mr-2" />
                Unpublish
              </Button>
            )}
            <Button variant="destructive" size="sm" onClick={() => setDeleteDialogOpen(true)}>
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
          </div>
        )}

        {/* Student Enroll Action */}
        {!isAdmin && !isEnrolled && project.status === 'published' && (
          <div className="flex items-center gap-3">
            <Button 
              size="sm" 
              onClick={handleEnroll}
              disabled={enrollMutation.isPending}
              className="bg-bits-blue hover:bg-blue-700"
            >
              <BookOpen className="w-4 h-4 mr-2" />
              {enrollMutation.isPending ? 'Enrolling...' : 'Enroll'}
            </Button>
          </div>
        )}
      </div>

      {/* Two-Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content Column (Left) - 2/3 width */}
        <div className="lg:col-span-2 space-y-6">
          {/* Project Overview */}
          <Card className="gap-4">
            <CardHeader>
              <div className="flex">
                <div className="h-6 w-6">
                  <Clipboard className="h-6 w-6 text-bits-blue" />
                </div>
                <CardTitle className="text-lg font-medium text-bits-blue-800 mt-0 ml-4">
                  Project Overview
                </CardTitle>
              </div>
            </CardHeader>

            <CardContent className="ml-1">
              <div
                className="ml-4 text-bits-grey-600 mb-4
  [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-2
  [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-2
  [&_li]:mb-1 [&_p]:mb-2"
                dangerouslySetInnerHTML={{
                  __html: project.project_overview || "-",
                }}
              />
              <Card className="my-2">
                <CardTitle className="text-xl font-medium text-bits-blue-800 mt-4 ml-8">
                  Description
                </CardTitle>
                <div
                  className="ml-8 mr-4 mb-4 text-bits-grey-600 
  [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-2
  [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-2
  [&_li]:mb-1 [&_p]:mb-2"
                  dangerouslySetInnerHTML={{
                    __html: project.description || "-",
                  }}
                />
              </Card>
              <Card className="mb-4">
                <CardTitle className="text-xl font-medium text-bits-blue-800 mt-4 ml-8">
                  Prerequisites & Requirements
                </CardTitle>
                <div
                  className="ml-8 mr-4 mb-4 text-bits-grey-600
  [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-2
  [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-2
  [&_li]:mb-1 [&_p]:mb-2"
                  dangerouslySetInnerHTML={{
                    __html: project.prerequisites || "-",
                  }}
                />
              </Card>
              <Card className="mb-4">
                <CardTitle className="text-xl font-medium text-bits-blue-800 mt-4 ml-8">
                  Learning Objectives
                </CardTitle>
                <div
                  className="ml-8 mr-4 mb-4 text-bits-grey-600
  [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-2
  [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-2
  [&_li]:mb-1 [&_p]:mb-2"
                  dangerouslySetInnerHTML={{
                    __html: project.learning_objectives || "-",
                  }}
                />
              </Card>
              <Card className="mb-4">
                <CardTitle className="text-xl font-medium text-bits-blue-800 mt-4 ml-8">
                  Instructions
                </CardTitle>
                <div
                  className="ml-8 mr-4 mb-4 text-bits-grey-600
  [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-2
  [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-2
  [&_li]:mb-1 [&_p]:mb-2"
                  dangerouslySetInnerHTML={{
                    __html: project.instructions || "-",
                  }}
                />
              </Card>
            </CardContent>
          </Card>

          {/* Q&A Module Card - Prominent Display */}
          {project.qaItems && project.qaItems.length > 0 && (
            <Card className="border-2 border-blue-200 bg-blue-50/30">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-blue-900">
                  <HelpCircle className="w-6 h-6" />
                  Self-Assessment Q&A
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {project.qaItems.map((qaItem: any, index: number) => (
                  <Card key={qaItem.id} className="bg-white">
                    <CardHeader>
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary" className="text-sm">
                          Q{index + 1}
                        </Badge>
                        <span className="font-semibold">{qaItem.question}</span>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                        <p className="text-sm font-medium text-green-900 mb-1">Answer:</p>
                        <p className="text-sm text-green-800">{qaItem.answer}</p>
                      </div>
                      {qaItem.explanation && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                          <p className="text-sm font-medium text-blue-900 mb-1">Explanation:</p>
                          <p className="text-sm text-bits-blue-neutral-800">{qaItem.explanation}</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar Column (Right) */}
        <div className="space-y-6">
          {/* Sandbox Actions Card */}
          {(isAdmin || isEnrolled) && (
            <Card className="border-blue-200 bg-blue-50/50">
              <CardContent className="pt-6">
                <div className="space-y-3">
                  <div className="flex items-center gap-2 mb-2">
                    <Server className="h-5 w-5 text-blue-600" />
                    <h3 className="text-sm font-semibold text-blue-900">Sandbox Environment</h3>
                  </div>

                  {/* 1st Step: Setup the local agent tunnel */}
                  {workspace.globalSandboxMode === "local" && (
                    <LocalRunButton projectId={projectId || ""} />
                  )}

                  {/* Agent Status Badge */}
                  {workspace.globalSandboxMode === "local" && workspace.workspaceType === "local" && (
                    <AgentStatusBadge projectId={projectId || ""} />
                  )}

                  <SandboxPanel
                    lifecycle={sandboxLifecycle}
                    onOpenSandbox={handleOpenSandbox}
                    projectParams={project}
                    agreed={agreed}
                    setAgreed={setAgreed}
                  />

                  {/* Progress & Mark as Completed (Students Only) */}
                  {!isAdmin && isEnrolled && (
                    <div className="pt-2 border-t border-blue-200 space-y-2">
                      {completionPercentage > 0 && (
                        <div>
                          <div className="flex justify-between text-xs text-blue-700 mb-1">
                            <span>Progress</span>
                            <span>{completionPercentage}%</span>
                          </div>
                          <Progress value={completionPercentage} className="h-2" />
                        </div>
                      )}
                      {enrollmentStatusValue === 'completed' ? (
                        <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-md">
                          <CheckCircle className="h-5 w-5 text-green-600" />
                          <span className="text-sm font-medium text-green-800">Project Completed!</span>
                        </div>
                      ) : (
                        <Button
                          variant="outline"
                          className="w-full border-green-500 text-green-700 hover:bg-green-50"
                          onClick={() => setMarkAsCompletedDialogOpen(true)}
                        >
                          <CheckCircle className="h-4 w-4 mr-2" />
                          Mark as Completed
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Quick Stats Card */}
          <Card>
            <CardContent className="pt-6">
              <div className="space-y-3 flex flex-col">
                <div className="flex items-start mb-2">
                  <BarChart className="h-6 w-6 text-bits-warning mr-2" />
                  <div className="text-lg font-medium text-bits-grey">
                    Quick Stats
                  </div>
                </div>

                {/* Difficulty */}
                <div className="flex justify-between items-start mb-2 gap-x-4">
                  <div className="flex justify-between items-start">
                    <Gauge className="h-6 w-6 mr-3 text-bits-grey-600" />
                    <p className="text-md text-bits-grey-600">
                      Difficulty
                    </p>
                  </div>
                  <p className="text-md text-bits-grey-600 capitalize">
                    {formatDifficulty(project.difficulty_level)}
                  </p>
                </div>

                {/* Sandbox Duration */}
                <div className="flex justify-between items-start mb-2">
                  <div className="flex justify-between items-start">
                    <Clock className="h-6 w-6 mr-3 text-bits-grey-600" />
                    <p className="text-md text-bits-grey-600">Sandbox Duration</p>
                  </div>
                  <p className="text-md text-bits-grey-600">
                    {project.sandbox_time_duration ? `${project.sandbox_time_duration} hrs` : '-'}
                  </p>
                </div>

                {/* Status */}
                <div className="flex justify-between items-start mb-2">
                  <div className="flex justify-between items-start">
                    <Flag className="h-6 w-6 mr-3 text-bits-grey-600" />
                    <p className="text-md text-bits-grey-600">Status</p>
                  </div>
                  <p className="text-md text-bits-grey-600 capitalize">
                    {formatStatus(project.status)}
                  </p>
                </div>

                {/* Created By */}
                {project.creator && (
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex justify-between items-start">
                      <User className="h-6 w-6 mr-3 text-bits-grey-600" />
                      <p className="text-md text-bits-grey-600">Created By</p>
                    </div>
                    <p className="text-md text-bits-grey-600">{project.creator.name}</p>
                  </div>
                )}

                {/* Published By */}
                {project.publishedBy && project.published_at && (
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex justify-between items-start">
                      <CheckCircle className="h-6 w-6 mr-3 text-bits-grey-600" />
                      <p className="text-md text-bits-grey-600">Published By</p>
                    </div>
                    <p className="text-md text-bits-grey-600">
                      {project.publishedBy.name} on {format(parseISO(project.published_at), 'dd MMM yyyy')}
                    </p>
                  </div>
                )}

                {/* Created At */}
                <div className="flex justify-between items-start mb-2">
                  <div className="flex justify-between items-start">
                    <Calendar className="h-6 w-6 mr-3 text-bits-grey-600" />
                    <p className="text-md text-bits-grey-600">Created</p>
                  </div>
                  <p className="text-md text-bits-grey-600">{formatRelativeTime(project.createdAt)}</p>
                </div>

                {/* Tags */}
                {project.tags && project.tags.length > 0 && (
                  <div className="flex flex-col gap-2 mt-3">
                    <div className="min-w-0">
                      <span className="block text-md font-medium text-bits-grey-600 break-words">
                        Tags:
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {project.tags.map((tag: string, index: number) => (
                        <div
                          key={index}
                          className="flex border border-bits-grey-300 bg-bits-grey-50 text-bits-blue-800 rounded-lg px-3 py-1 text-sm font-medium"
                        >
                          {tag}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Course Information Card */}
          {project.course && (
            <Card className="gap-4">
              <CardHeader>
                <div className="flex">
                  <div className="h-6 w-6">
                    <FileText className="h-6 w-6 text-bits-blue" />
                  </div>
                  <CardTitle className="text-lg text-bits-grey font-medium mt-0 ml-4">
                    Course Information
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent className="ml-1">
                <p className="text-bits-blue-neutral-900 text-md font-semibold mb-1">
                  {project.course.context_title || '-'}
                </p>
                <p className="text-bits-grey-600 text-sm mb-1">
                  {project.course.context_label || '-'}
                </p>
              </CardContent>
            </Card>
          )}

          {/* Resource Profile Details Card */}
          {project.resourceProfile && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center text-xl text-bits-blue font-semibold">
                  <Settings className="h-6 w-6 mr-2 text-bits-warning" />
                  <span className="text-lg font-medium text-bits-blue-800">
                    Resource Profile Details
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="-mt-2">
                <div className="space-y-3 flex flex-col">
                  {project.resourceProfile.name && (
                    <div className="mb-4 pb-3 border-b border-bits-grey-200">
                      <h3 className="text-lg font-semibold text-bits-blue-800">
                        Name: {project.resourceProfile.name}
                      </h3>
                    </div>
                  )}
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1 min-w-0">
                      <span className="block text-md font-medium text-bits-blue-800 break-words">
                        Resource Type
                      </span>
                    </div>
                    <div className="text-md capitalize font-medium text-bits-grey-600 whitespace-nowrap">
                      {project.resourceProfile.resource_type || "N/A"}
                    </div>
                  </div>

                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1 min-w-0">
                      <span className="block text-md font-medium text-bits-blue-800 break-words">
                        Memory (RAM)
                      </span>
                    </div>
                    <div className="text-md font-medium text-bits-grey-600 whitespace-nowrap">
                      {project.resourceProfile.ram_gb
                        ? `${project.resourceProfile.ram_gb} GB`
                        : "N/A"}
                    </div>
                  </div>

                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1 min-w-0">
                      <span className="block text-md font-medium text-bits-blue-800 break-words">
                        CPU Cores
                      </span>
                    </div>
                    <div className="text-md font-medium text-bits-grey-600 whitespace-nowrap">
                      {project.resourceProfile.cpu_cores || "N/A"}
                    </div>
                  </div>

                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1 min-w-0">
                      <span className="block text-md font-medium text-bits-blue-800 break-words">
                        Disk Space
                      </span>
                    </div>
                    <div className="text-md font-medium text-bits-grey-600 whitespace-nowrap">
                      {project.resourceProfile.disk_gb
                        ? `${project.resourceProfile.disk_gb} GB`
                        : "N/A"}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Project Files Section */}
          <Card>
            <ProjectFilesSection 
              files={[]}
              datasets={project?.datasets || []}
            />
          </Card>
        </div>
      </div>

      {/* Confirmation Dialogs */}
      <AlertDialog open={publishDialogOpen} onOpenChange={setPublishDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Publish Practice Project?</AlertDialogTitle>
            <AlertDialogDescription>
              This will make the practice project visible to all students. They will be able to enroll and start working on it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handlePublish} disabled={publishMutation.isPending}>
              {publishMutation.isPending ? 'Publishing...' : 'Publish'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={unpublishDialogOpen} onOpenChange={setUnpublishDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unpublish Practice Project?</AlertDialogTitle>
            <AlertDialogDescription>
              {project.enrollmentCount && project.enrollmentCount > 0 ? (
                <>
                  <strong>{project.enrollmentCount} students</strong> are currently enrolled. 
                  Unpublishing will hide this project but won't affect their existing progress.
                </>
              ) : (
                'This will hide the practice project from students. You can publish it again later.'
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleUnpublish} disabled={unpublishMutation.isPending}>
              {unpublishMutation.isPending ? 'Unpublishing...' : 'Unpublish'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Practice Project?</AlertDialogTitle>
            <AlertDialogDescription>
              This action will soft-delete the practice project. All associated data including enrollments,
              Q&A items, and student progress will be hidden. This action can be reversed by an administrator.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete} 
              disabled={deleteMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={markAsCompletedDialogOpen} onOpenChange={setMarkAsCompletedDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Mark Project as Completed?</AlertDialogTitle>
            <AlertDialogDescription>
              This will set your progress to 100% and mark this practice project as completed.
              You can still access the sandbox and project materials after completion.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleMarkAsCompleted}
              disabled={markAsCompletedMutation.isPending}
              className="bg-green-600 text-white hover:bg-green-700"
            >
              {markAsCompletedMutation.isPending ? 'Saving...' : 'Mark as Completed'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <SandboxTermsModal open={termsOpen} onOpenChange={setTermsOpen} />
    </div>
  );
}

// Skeleton Loader
function DetailsPageSkeleton() {
  return (
    <div className="space-y-6 p-6">
      {/* Header Skeleton */}
      <div className="flex flex-row items-start gap-3 mb-6 mt-4">
        <Skeleton className="h-8 w-8 rounded-full" />
        <div className="flex flex-col flex-grow">
          <Skeleton className="h-9 w-2/3 mb-2" />
          <div className="flex gap-2">
            <Skeleton className="h-6 w-24" />
            <Skeleton className="h-6 w-24" />
          </div>
        </div>
      </div>

      {/* Two-Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content Column (Left) - 2/3 width */}
        <div className="lg:col-span-2 space-y-6">
          {/* Project Overview Card */}
          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-48" />
            </CardHeader>
            <CardContent className="space-y-4">
              <Skeleton className="h-24 w-full" />
              {/* Description */}
              <div className="space-y-3">
                <Skeleton className="h-6 w-32" />
                <Skeleton className="h-20 w-full" />
              </div>
              {/* Prerequisites */}
              <div className="space-y-3">
                <Skeleton className="h-6 w-48" />
                <Skeleton className="h-16 w-full" />
              </div>
              {/* Learning Objectives */}
              <div className="space-y-3">
                <Skeleton className="h-6 w-40" />
                <Skeleton className="h-16 w-full" />
              </div>
              {/* Instructions */}
              <div className="space-y-3">
                <Skeleton className="h-6 w-32" />
                <Skeleton className="h-16 w-full" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar Column (Right) */}
        <div className="space-y-6">
          {/* Sandbox Environment Card */}
          <Card>
            <CardContent className="pt-6 space-y-4">
              <Skeleton className="h-5 w-48" />
              <Skeleton className="h-10 w-full" />
            </CardContent>
          </Card>

          {/* Quick Stats Card */}
          <Card>
            <CardContent className="pt-6 space-y-3">
              <Skeleton className="h-6 w-32 mb-4" />
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-4 w-20" />
                </div>
                <div className="flex justify-between items-center">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-4 w-16" />
                </div>
                <div className="flex justify-between items-center">
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-4 w-24" />
                </div>
                <div className="flex justify-between items-center">
                  <Skeleton className="h-4 w-28" />
                  <Skeleton className="h-4 w-32" />
                </div>
                <div className="flex justify-between items-center">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-4 w-28" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Resource Profile Details Card */}
          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-48" />
            </CardHeader>
            <CardContent className="space-y-3">
              <Skeleton className="h-5 w-40 mb-3" />
              <div className="space-y-2">
                <div className="flex justify-between">
                  <Skeleton className="h-4 w-28" />
                  <Skeleton className="h-4 w-20" />
                </div>
                <div className="flex justify-between">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-4 w-16" />
                </div>
                <div className="flex justify-between">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-4 w-12" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Project Files Card */}
          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-32" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-32 w-full" />
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
